import { useRouter } from 'next/router';
import { NextPage } from 'next';
import { useEffect } from 'react';

import { useUser } from 'context/user';
import { AdminRoutes, ContractorRoutes, AuthRoutes, adminRoutesByPath, workerRoutesByPath } from 'routes';
import LoadingScreen from 'components/container-components/LoadingScreen';
import { useCompany } from 'context/company';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { useCheckCompanySWR, useCheckEnrollmentProfile } from 'hooks/useCheckSWR';
import { useCheckEmployeesSWR } from 'hooks/useCheckEmployeeSWR';
import { Error404Component } from 'pages/404';

// Define a mapping of allowed routes for each user type
const allowedRoutes = {
  manager: Object.values(AdminRoutes),
  worker: Object.values(ContractorRoutes),
};

const isAdminRoute = (route: string) => {
  return allowedRoutes['manager'].includes(route);
};

const isWorkerRoute = (route: string) => {
  return allowedRoutes['worker'].includes(route);
};

const AuthorizationCheck = <P extends object>(Component: React.ComponentType<P>) => {
  const UserCheck: NextPage<P> = (props) => {
    const router = useRouter();
    const isTheraStartRoute = router.pathname.includes(AuthRoutes.TheraStart);

    const { company, isLoading: isCompanyLoading } = useCompany();
    const checkCompanyId = company?.w2Payroll?.checkId;
    const { isPending: isCheckCompanyLoading } = useCheckCompanySWR({
      checkCompanyId,
    });
    const { isPending: isEnrollmentPending } = useCheckEnrollmentProfile({
      checkCompanyId,
    });
    const { isPending: isCheckEmployeesLoading } = useCheckEmployeesSWR({
      checkCompanyId: company?.w2Payroll?.checkId,
    });
    const {
      user,
      isLoading: isUserLoading,
      roleType,
      isLoggingOut,
      isAccountSwitchingInProgress,
      setIsAccountSwitchingInProgress,
      is2FAVerified,
    } = useUser();
    const { isAllowed, isMyWLCLoading } = useRouteAuthorization();
    const isManager = roleType === 'manager';

    /**
     * Note:
     * When a user switches roles (e.g., from 'manager' to 'worker' or vice versa),
     * the roleType state gets updated promptly. However, as the router
     * takes a moment to navigate to the new role's dashboard, there's a brief
     * instance where the AuthorizationCheck component runs with the existing route
     * but new roleType and briefly displays a 404 error before the router completes
     * the transition.
     * Using the userContext to keep track of switching progress resolves the issue
     */
    // if page has loaded setIsAccountSwitchingInProgress to false
    useEffect(() => {
      const handleContentLoaded = () => {
        setIsAccountSwitchingInProgress(false);
      };

      router.events.on('routeChangeComplete', handleContentLoaded);

      return () => {
        router.events.off('routeChangeComplete', handleContentLoaded);
      };
    }, [router.events, setIsAccountSwitchingInProgress]);

    if (
      isUserLoading ||
      (is2FAVerified && !isTheraStartRoute ? !roleType : false) ||
      isLoggingOut ||
      isAccountSwitchingInProgress ||
      !user ||
      (isManager ? !company : false) ||
      isCompanyLoading ||
      isCheckCompanyLoading ||
      isEnrollmentPending ||
      isCheckEmployeesLoading ||
      // Wait for WLC affiliations SWR so `isWhiteLabelClientAdmin` is final before the route
      // access check runs. Skipped for non-org-admins (hook short-circuits → isLoading stays false),
      // so only org admins pay this one-time cost.
      isMyWLCLoading
    ) {
      return <LoadingScreen />;
    }

    if (user) {
      if (isManager) {
        if (!isAdminRoute(router.pathname)) {
          return <Error404Component />;
        }

        const routeInfo = adminRoutesByPath[router.pathname];
        if (routeInfo && !isAllowed(routeInfo.access)) {
          console.info(`Access denied for ${router.pathname} route with access - ${JSON.stringify(routeInfo.access)}`);
          return <Error404Component />;
        }
      } else {
        if (isTheraStartRoute) {
          return <Component {...props} />;
        }
        if (!isWorkerRoute(router.pathname)) {
          return <Error404Component />;
        }

        const routeInfo = workerRoutesByPath[router.pathname];
        if (routeInfo && !isAllowed(routeInfo.access)) {
          return <Error404Component />;
        }
      }
    }

    return <Component {...props} />;
  };

  return UserCheck;
};

export default AuthorizationCheck;
